/** Marble position data sent each frame */
export interface MarbleFrame {
  id: number;
  x: number;
  y: number;
  a: number; // angle
  c: number; // cooltime ratio (0~1), -1 = no skill gauge
}

/** Entity state sent each frame (angle + alive) */
export interface EntityFrame {
  angle: number;
  alive: boolean;
}

export interface DecodedFrame {
  marbles: MarbleFrame[];
  winners: string[];
  camera: { x: number; y: number; zoom: number };
  entities: EntityFrame[];
}

/**
 * Encode marble frame data into a compact binary format.
 *
 * Binary format:
 *   [marbleCount(u16)][winnersCount(u16)][entityCount(u16)]
 *   [cam.x(f32)][cam.y(f32)][cam.zoom(f32)]
 *   then per marble: [id(u16)][x(f32)][y(f32)][angle(f32)]
 *   then per winner: [nameLength(u16)][nameBytes(utf8)]
 *   then per entity: [alive(u8)][angle(f32)]
 */
export function encodeFrame(
  marbles: MarbleFrame[],
  winners: string[],
  camera: { x: number; y: number; zoom: number },
  entities: EntityFrame[] = [],
): ArrayBuffer {
  const winnersEncoded = winners.map(n => new TextEncoder().encode(n));
  const winnersBytes = winnersEncoded.reduce((sum, e) => sum + 2 + e.byteLength, 0);

  const headerSize = 2 + 2 + 2 + 12; // marbleCount + winnersCount + entityCount + camera(3xf32)
  const marbleSize = 2 + 16; // id(u16) + x,y,angle,cool (4xf32)
  const entitySize = 1 + 4; // alive(u8) + angle(f32)
  const totalSize = headerSize + marbles.length * marbleSize + winnersBytes + entities.length * entitySize;

  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);
  let offset = 0;

  view.setUint16(offset, marbles.length, true); offset += 2;
  view.setUint16(offset, winners.length, true); offset += 2;
  view.setUint16(offset, entities.length, true); offset += 2;
  view.setFloat32(offset, camera.x, true); offset += 4;
  view.setFloat32(offset, camera.y, true); offset += 4;
  view.setFloat32(offset, camera.zoom, true); offset += 4;

  for (const m of marbles) {
    view.setUint16(offset, m.id, true); offset += 2;
    view.setFloat32(offset, m.x, true); offset += 4;
    view.setFloat32(offset, m.y, true); offset += 4;
    view.setFloat32(offset, m.a, true); offset += 4;
    view.setFloat32(offset, m.c, true); offset += 4;
  }

  for (const encoded of winnersEncoded) {
    view.setUint16(offset, encoded.byteLength, true); offset += 2;
    new Uint8Array(buffer, offset, encoded.byteLength).set(encoded);
    offset += encoded.byteLength;
  }

  for (const e of entities) {
    view.setUint8(offset, e.alive ? 1 : 0); offset += 1;
    view.setFloat32(offset, e.angle, true); offset += 4;
  }

  return buffer;
}

/** Decode binary frame data back into structured objects */
export function decodeFrame(data: ArrayBuffer): DecodedFrame {
  const view = new DataView(data);
  let offset = 0;

  const marbleCount = view.getUint16(offset, true); offset += 2;
  const winnersCount = view.getUint16(offset, true); offset += 2;
  const entityCount = view.getUint16(offset, true); offset += 2;
  const camX = view.getFloat32(offset, true); offset += 4;
  const camY = view.getFloat32(offset, true); offset += 4;
  const camZoom = view.getFloat32(offset, true); offset += 4;

  const marbles: MarbleFrame[] = [];
  for (let i = 0; i < marbleCount; i++) {
    const id = view.getUint16(offset, true); offset += 2;
    const x = view.getFloat32(offset, true); offset += 4;
    const y = view.getFloat32(offset, true); offset += 4;
    const a = view.getFloat32(offset, true); offset += 4;
    const c = view.getFloat32(offset, true); offset += 4;
    marbles.push({ id, x, y, a, c });
  }

  const decoder = new TextDecoder();
  const winners: string[] = [];
  for (let i = 0; i < winnersCount; i++) {
    const len = view.getUint16(offset, true); offset += 2;
    const nameBytes = new Uint8Array(data, offset, len);
    winners.push(decoder.decode(nameBytes));
    offset += len;
  }

  const entities: EntityFrame[] = [];
  for (let i = 0; i < entityCount; i++) {
    const alive = view.getUint8(offset) === 1; offset += 1;
    const angle = view.getFloat32(offset, true); offset += 4;
    entities.push({ alive, angle });
  }

  return { marbles, winners, camera: { x: camX, y: camY, zoom: camZoom }, entities };
}
